import { useCallback, useMemo } from 'react';

import { createGlobalStyle } from 'styled-components';

import { AgentRolesPickerProps, CheckboxOptions } from './AgentRolesPicker.decl';
import { AgentRolesPickerLoading } from './AgentRolesPickerLoading';

import {
  Box,
  Checkbox,
  Flex,
  Spacer,
  Tooltip,
  Typography,
  ThemeProvider,
  Banner,
  BannerIcon,
  BannerHeading,
} from '@aircall/tractor-v2';
import { dashboardThemeV2 } from '@config/theme/theme';
import { ROLE_NAME } from '@constants/permissions.constants';
import {
  GetAgentAvailableRoles,
  GetAgentAvailableRoles_getAvailableRolesForAgent,
} from '@generated/GetAgentAvailableRoles';
import { GET_AGENT_AVAILABLE_ROLES } from '@graphql/queries/GetAgentAvailableRoles';
import { useCompanyRoles } from '@hooks/useCompanyRoles/useCompanyRoles';
import { useGraphQuery } from '@hooks/useQuery';
import { Field, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';

// Global style because data-test is not customizable on Tooltip right now
const TooltipGlobalStyle = createGlobalStyle`
  div[data-test="tooltip-content"] {
    transform: translateX(-9px);
  }
`;

export function CheckboxOption({
  label,
  onChange,
  checkboxName,
  value,
  description,
  disabled,
  tooltipText,
  checkboxDisabled = false,
}: CheckboxOptions) {
  const displayTooltip = disabled && tooltipText;

  const content = (
    <Spacer space='xxs' data-test={`checkbox-option-${checkboxName}`}>
      <Flex h={24} justifyContent='center' alignItems='center'>
        <Checkbox
          size='small'
          data-test={`checkbox-option-${checkboxName}-${disabled ? 'disabled' : 'enabled'}`}
          disabled={disabled || checkboxDisabled}
          checked={value}
          onChange={onChange}
        />
      </Flex>
      <Flex flexDirection='column'>
        <Spacer space='xs'>
          <Typography variant='bodySemiboldM' color={disabled ? 'text-placeholder' : 'text-base'}>
            {label}
          </Typography>
        </Spacer>
        <Typography color={disabled ? 'text-placeholder' : 'text-base'}>{description}</Typography>
      </Flex>
    </Spacer>
  );

  if (displayTooltip) {
    return (
      <>
        <TooltipGlobalStyle />
        <Tooltip mouseLeaveDelay={0} title={tooltipText} side='top' align='start'>
          {content}
        </Tooltip>
      </>
    );
  }

  return content;
}

export function AgentRolesPicker({
  name,
  disabled,
  showInfoBanner = false,
}: AgentRolesPickerProps) {
  const { t } = useTranslation();
  const { data, loading: availableRolesLoading } =
    useGraphQuery<GetAgentAvailableRoles>(GET_AGENT_AVAILABLE_ROLES);
  const availableRoles = data?.getAvailableRolesForAgent || [];

  const { companyRoles, loading } = useCompanyRoles();

  const roles: GetAgentAvailableRoles_getAvailableRolesForAgent[] = useMemo(() => {
    // used to filter and order
    const arrayOfRolesToBeDisplayed = [ROLE_NAME.OWNER, ROLE_NAME.ADMIN, ROLE_NAME.SUPERVISOR];
    // we ignore here as this only exists for when the api fails
    /* istanbul ignore next */
    if (!companyRoles.length) {
      return [];
    }

    return arrayOfRolesToBeDisplayed.map(
      (item) => companyRoles.find((role) => item === role.name)!
    );
  }, [companyRoles]);

  const isRoleAvailable = (roleId: string) =>
    availableRoles.find((availableRole) => availableRole?.ID === roleId);

  const form = useForm();
  const fieldState = form.getFieldState(name);
  const _showInfoBanner = showInfoBanner && fieldState?.dirty;

  // Custom on change function
  const onChange = useCallback(
    (fieldName: string) => (checked: boolean) => {
      // If the field is initialized without any value this might happen but it is very unlikely
      /* istanbul ignore next */
      const fieldValue = fieldState?.value || [];

      const role = companyRoles.find((item) => item.name === fieldName);

      if (checked && !!role) {
        form.change(name, [...fieldValue, role]);
      } else {
        form.change(
          name,
          fieldValue.filter(
            (item: GetAgentAvailableRoles_getAvailableRolesForAgent) => item.name !== fieldName
          )
        );
      }
    },
    [companyRoles, fieldState?.value, form, name]
  );

  const agentRole = useMemo(
    () => companyRoles.find((item) => item.name === ROLE_NAME.AGENT),
    [companyRoles]
  );

  const isSelected = useCallback(
    (id?: string) => {
      const fieldValue = fieldState?.value;
      if (!fieldValue) {
        return false;
      }

      return fieldValue.some(
        (item: GetAgentAvailableRoles_getAvailableRolesForAgent) => item?.ID === id
      );
    },
    [fieldState]
  );

  return (
    <ThemeProvider theme={dashboardThemeV2}>
      <Field name={name}>
        {() => (
          <Spacer direction='vertical' space='s' fluid>
            <Box padding='s' backgroundColor='white' borderRadius='8px'>
              <Typography variant='bodySemiboldM'>{t('roles_picker.section1.title')}</Typography>
              <CheckboxOption
                value={isSelected(agentRole?.ID)}
                checkboxDisabled
                label={t(`roles_picker.roles.Agent.title`)}
                description={t(`roles_picker.roles.Agent.description`)}
              />
            </Box>

            {loading || availableRolesLoading ? (
              <AgentRolesPickerLoading />
            ) : (
              <Box padding='s' backgroundColor='white' borderRadius='8px'>
                <Spacer space='s' direction='vertical'>
                  <Flex flexDirection='row' justifyContent='space-between'>
                    <Typography variant='bodySemiboldM'>
                      {t('roles_picker.section2.title')}
                    </Typography>
                  </Flex>
                  {roles.map((item) => (
                    <CheckboxOption
                      value={isSelected(item.ID)}
                      key={item.ID}
                      tooltipText={t(`roles_picker.roles.${item.name}.tooltip_text`)}
                      checkboxName={item.name}
                      onChange={onChange(item.name)}
                      disabled={!isRoleAvailable(item.ID)}
                      label={t(`roles_picker.roles.${item.name}.title`)}
                      description={t(`roles_picker.roles.${item.name}.description`)}
                      checkboxDisabled={disabled}
                    />
                  ))}
                </Spacer>
              </Box>
            )}

            {_showInfoBanner && (
              <Banner variant='info' inline data-test='update-roles-banner'>
                <BannerIcon />
                <BannerHeading>{t('roles_picker.info_banner_text')}</BannerHeading>
              </Banner>
            )}
          </Spacer>
        )}
      </Field>
    </ThemeProvider>
  );
}
